import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const BACKGROUND_SYMBOL = Object.freeze({
  imagePath: "port/generated/symbols/30/png/1.png",
  layout: {
    imageWidth: 5564.9,
    imageHeight: 2186.2,
    imageOffsetX: -1808.5,
    imageOffsetY: -616.65,
  },
  pngLayout: {
    sourceWidth: 5595,
    sourceHeight: 2217,
    cropX: 8,
    cropY: 8,
    cropWidth: 5578,
    cropHeight: 2201,
  },
});

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL5_BODY_LAYOUT_FIX = Object.freeze({
  greenPlatform: { x: 363.45, y: 170.5 },
  blueBarier: { x: -126.95, y: 308.0 },
  mainPlatform: { x: -116.95, y: 291.95 },
  rightBarier: { x: 447.95, y: 41.0 },
});

const LEVEL5_SYMBOLS = Object.freeze({
  greenPlatform: {
    imagePath: "port/generated/symbols/90/png/1.png",
    width: 242.6,
    height: 196.6,
    offsetX: -92.55,
    offsetY: -91.3,
  },
  blueBarier: {
    imagePath: "port/generated/symbols/100/png/1.png",
    width: 337.65,
    height: 216.7,
    offsetX: -258.35,
    offsetY: -59.35,
  },
  mainPlatform: {
    imagePath: "port/generated/symbols/103/png/1.png",
    width: 671.2,
    height: 300.5,
    offsetX: -29.3,
    offsetY: -82.5,
  },
  rightBarier: {
    imagePath: "port/generated/symbols/106/png/1.png",
    width: 220.7,
    height: 278.25,
    offsetX: -75.0,
    offsetY: -21.15,
  },
  littleBall: {
    imagePath: "port/generated/symbols/111/png/1.png",
    width: 199.95,
    height: 100.05,
    offsetX: -78.4,
    offsetY: -66.15,
  },
  jumpPlatform: {
    imagePath: "port/generated/symbols/115/png/1.png",
    width: 398.9,
    height: 206.5,
    offsetX: -77.45,
    offsetY: -136.2,
  },
  bigBall: {
    imagePath: "port/generated/symbols/119/png/1.png",
    width: 403.75,
    height: 266.2,
    offsetX: -87.55,
    offsetY: -130.1,
  },
  firstPlatform: {
    imagePath: "port/generated/symbols/122/png/1.png",
    width: 502.95,
    height: 300.5,
    offsetX: -88.1,
    offsetY: -141.5,
  },
});

const BLUE_CHECK_SYMBOL = Object.freeze({
  imageWidth: 11.0,
  imageHeight: 11.0,
  imageOffsetX: -5.5,
  imageOffsetY: -5.5,
});

const GREEN_CHECK_SYMBOL = Object.freeze({
  imageWidth: 11.0,
  imageHeight: 11.0,
  imageOffsetX: -5.0,
  imageOffsetY: -5.55,
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelFiveRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_5/1.visual.runtime.svg";
      this.backgroundImage = new Image();
      this.backgroundImage.src = BACKGROUND_SYMBOL.imagePath;
      this.backgroundStart = this.levelData.layout.namedChildren.backGroundMC;
      this.level5Images = Object.fromEntries(
        Object.entries(LEVEL5_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
      this.blueCheckFrames = [1, 2].map((frame) => {
        const image = new Image();
        image.src = `port/generated/symbols/93/png/DefineSprite_93/${frame}.png`;
        return image;
      });
      this.greenCheckFrames = [1, 2].map((frame) => {
        const image = new Image();
        image.src = `port/generated/symbols/96/png/DefineSprite_96/${frame}.png`;
        return image;
      });
    } else {
      this.level5Images = {};
      this.blueCheckFrames = [];
      this.greenCheckFrames = [];
    }

    for (const [name, fix] of Object.entries(LEVEL5_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.createAllLevelBodies();
    this.blueBarierBody = this.getBody("blueBarier");
    this.greenPlatformBody = this.getBody("greenPlatform");

    this.setNamedObjectFrame("blueCheck", 1);
    this.setNamedObjectFrame("greenCheck", 1);
  }

  levelUpdate() {
    if (this.player.y > 650) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    if (this.hitNamedObject("blueCheck") && this.blueBarierBody) {
      this.setNamedObjectFrame("blueCheck", 2);
      this.destroyBody(this.blueBarierBody);
      this.blueBarierBody = null;
    }
    if (this.hitNamedObject("greenCheck") && this.greenPlatformBody) {
      this.setNamedObjectFrame("greenCheck", 2);
      this.destroyBody(this.greenPlatformBody);
      this.greenPlatformBody = null;
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart) {
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      const sym = BACKGROUND_SYMBOL;
      ctx.save();
      ctx.translate(bx, by);
      const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
      const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
      ctx.drawImage(
        this.backgroundImage,
        sym.pngLayout.cropX, sym.pngLayout.cropY,
        sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
        sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
        sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
        sym.pngLayout.cropWidth * scaleX,
        sym.pngLayout.cropHeight * scaleY,
      );
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    if (this.greenPlatformBody) {
      const greenPlatformLayout = this.getDisplayLayout("greenPlatform");
      drawSymbolImage(
        ctx,
        this.level5Images.greenPlatform,
        LEVEL5_SYMBOLS.greenPlatform,
        greenPlatformLayout.x,
        greenPlatformLayout.y,
        greenPlatformLayout.rotation ?? 0,
      );
    }

    const blueCheckLayout = this.getDisplayLayout("blueCheck");
    const blueCheckFrame = this.blueCheckFrames[this.getNamedObjectFrame("blueCheck") - 1];
    drawAnimatedFrame(ctx, blueCheckFrame, BLUE_CHECK_SYMBOL, blueCheckLayout.x, blueCheckLayout.y);

    const greenCheckLayout = this.getDisplayLayout("greenCheck");
    const greenCheckFrame = this.greenCheckFrames[this.getNamedObjectFrame("greenCheck") - 1];
    drawAnimatedFrame(ctx, greenCheckFrame, GREEN_CHECK_SYMBOL, greenCheckLayout.x, greenCheckLayout.y);

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.blueBarierBody) {
      const blueBarierLayout = this.getDisplayLayout("blueBarier");
      drawSymbolImage(
        ctx,
        this.level5Images.blueBarier,
        LEVEL5_SYMBOLS.blueBarier,
        blueBarierLayout.x,
        blueBarierLayout.y,
        blueBarierLayout.rotation ?? 0,
      );
    }

    for (const name of ["mainPlatform", "rightBarier", "littleBall", "jumpPlatform", "bigBall", "firstPlatform"]) {
      const layout = this.getDisplayLayout(name);
      drawSymbolImage(
        ctx,
        this.level5Images[name],
        LEVEL5_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
